/**
 * ReverseAirdrop Component
 *
 * Allows administrators to reverse (claw back) previously executed airdrops.
 * Displays successful transfers and provides selection mechanism for reversal.
 *
 * @component
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { getRequest, postRequest } from "../../utils/httpRequest";

// Shared imports
import { ITEMS_PER_PAGE, API_ENDPOINTS } from "./constants";
import {
  formatDate,
  formatAmount,
  getHashscanUrl,
  calculateReverseStats,
} from "./utils";
import { colors, gradients, commonStyles, spinnerKeyframes } from "./styles";
import {
  Pagination,
  StatsCard,
  StatsContainer,
  LoadingSpinner,
  SectionHeader,
  FilterBar,
  SearchInput,
  EmptyState,
  GhostButton,
  DangerButton,
  SecondaryButton,
  ConfirmationModal,
  WarningBox,
  WarningList,
} from "./components";

// ============================================================================
// Sub-components
// ============================================================================

/**
 * ReverseStats - Statistics display for reversible airdrops
 */
const ReverseStats = ({ history, selectedItems, filteredHistory }) => {
  const stats = useMemo(
    () => calculateReverseStats(history, selectedItems, filteredHistory),
    [history, selectedItems, filteredHistory],
  );

  return (
    <StatsContainer>
      <StatsCard
        value={stats.totalReversible}
        label="Reversible Transfers"
        variant="primary"
      />
      <StatsCard
        value={stats.selectedCount}
        label="Selected"
        variant={stats.selectedCount > 0 ? "accent" : "gray"}
      />
      <StatsCard
        value={formatAmount(stats.totalAmount)}
        label="Total Amount"
        variant="success"
      />
      <StatsCard
        value={stats.uniqueTokens}
        label="Unique Tokens"
        variant="info"
      />
    </StatsContainer>
  );
};

/**
 * ReverseTable - Table displaying reversible transfers with selection
 */
const ReverseTable = ({
  transfers,
  selectedItems,
  onToggleSelect,
  onSelectPage,
  pageAllSelected,
  hederaNetwork,
}) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderRadius: "0 0 6px 6px",
      overflow: "hidden",
    }}
  >
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "13px",
        }}
      >
        <thead>
          <tr style={{ background: gradients.primary }}>
            <th style={{ ...commonStyles.tableHeaderCell, width: "40px" }}>
              <input
                type="checkbox"
                checked={pageAllSelected && transfers.length > 0}
                onChange={onSelectPage}
                title="Select all on this page"
                style={{ cursor: "pointer" }}
              />
            </th>
            <th style={commonStyles.tableHeaderCell}>Recipient</th>
            <th style={commonStyles.tableHeaderCell}>Token ID</th>
            <th style={{ ...commonStyles.tableHeaderCell, textAlign: "right" }}>
              Amount
            </th>
            <th style={commonStyles.tableHeaderCell}>Transaction</th>
            <th style={commonStyles.tableHeaderCell}>Date</th>
          </tr>
        </thead>
        <tbody>
          {transfers.map((item, idx) => {
            const isSelected = selectedItems.includes(item.id);
            return (
              <tr
                key={item.id}
                onClick={() => onToggleSelect(item.id)}
                style={{
                  background: isSelected
                    ? "#fff3e0"
                    : idx % 2 === 0
                    ? colors.white
                    : "#f9f9f9",
                  cursor: "pointer",
                  transition: "background 0.15s ease",
                  borderBottom: `1px solid ${colors.border}`,
                }}
              >
                <td
                  style={commonStyles.tableCell}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect(item.id)}
                    style={{ cursor: "pointer" }}
                  />
                </td>
                <td style={commonStyles.tableCell}>
                  <code
                    style={{
                      ...commonStyles.code,
                      fontSize: "12px",
                    }}
                  >
                    {item.recipientAccountId}
                  </code>
                </td>
                <td style={commonStyles.tableCell}>
                  <code
                    style={{
                      background: "#e8f4fd",
                      padding: "4px 8px",
                      borderRadius: "3px",
                      fontSize: "12px",
                      color: "#0066cc",
                    }}
                  >
                    {item.tokenId}
                  </code>
                </td>
                <td
                  style={{
                    ...commonStyles.tableCell,
                    textAlign: "right",
                    fontWeight: 600,
                    color: colors.primary,
                  }}
                >
                  {formatAmount(item.amount)}
                </td>
                <td style={commonStyles.tableCell}>
                  {item.transactionId ? (
                    <a
                      href={getHashscanUrl(item.transactionId, hederaNetwork)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      title={item.transactionId}
                      style={{
                        color: "#0066cc",
                        textDecoration: "none",
                        fontSize: "12px",
                      }}
                    >
                      {item.transactionId.length > 20
                        ? `${item.transactionId.substring(0, 20)}...`
                        : item.transactionId}
                    </a>
                  ) : (
                    "-"
                  )}
                </td>
                <td
                  style={{
                    ...commonStyles.tableCell,
                    color: colors.gray600,
                    fontSize: "12px",
                  }}
                >
                  {formatDate(item.createdAt)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

/**
 * ResultsInfo - Shows count and selection info
 */
const ResultsInfo = ({ totalCount, selectedCount }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px 16px",
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderBottom: "none",
      borderRadius: "6px 6px 0 0",
    }}
  >
    <span style={{ fontSize: "13px", color: colors.gray600 }}>
      <b style={{ color: colors.primary }}>{totalCount}</b> reversible airdrop
      {totalCount !== 1 ? "s" : ""} found
    </span>
    {selectedCount > 0 && (
      <span
        style={{
          padding: "4px 12px",
          background: colors.accent,
          color: colors.white,
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 600,
        }}
      >
        {selectedCount} selected
      </span>
    )}
  </div>
);

// ============================================================================
// Main Component
// ============================================================================

const ReverseAirdrop = ({
  currencies,
  showNotification,
  hederaNetwork = "testnet",
  payoutWalletId,
}) => {
  // State
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reversing, setReversing] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [apiMessage, setApiMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch history on mount
  useEffect(() => {
    fetchHistory();
  }, []);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Clear selection when search changes
  useEffect(() => {
    setSelectedItems([]);
  }, [searchQuery]);

  // Data fetching
  const fetchHistory = async () => {
    setLoading(true);
    setApiMessage(null);
    try {
      const data = await getRequest(API_ENDPOINTS.AIRDROP_HISTORY);
      if (data.success) {
        // Only show successful transfers that can be reversed
        const reversibleTransfers = (data.data?.transfers || []).filter(
          (t) => t.status === "success",
        );
        setHistory(reversibleTransfers);
        if (data.data?.message) {
          setApiMessage(data.data.message);
        }
        if (data.data?.error) {
          setApiMessage(`API Error: ${data.data.error}`);
        }
      }
    } catch (err) {
      console.error("Error fetching history:", err);
      setHistory([]);
      setApiMessage("Unable to fetch history from the liquidity pool API");
    } finally {
      setLoading(false);
    }
  };

  // Filtered history (memoized)
  const filteredHistory = useMemo(() => {
    if (!searchQuery) return history;
    const query = searchQuery.toLowerCase();
    return history.filter(
      (h) =>
        h.recipientAccountId?.toLowerCase().includes(query) ||
        h.transactionId?.toLowerCase().includes(query),
    );
  }, [history, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredHistory.length / ITEMS_PER_PAGE);
  const paginatedHistory = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredHistory.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredHistory, currentPage]);

  // Unique token IDs for reversal
  const uniqueTokenIds = useMemo(() => {
    return [...new Set(history.map((h) => h.tokenId))].filter(Boolean);
  }, [history]);

  // Selection handlers
  const toggleSelectItem = useCallback((id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  }, []);

  const selectPageItems = useCallback(() => {
    const pageIds = paginatedHistory.map((h) => h.id);
    const allPageSelected = pageIds.every((id) => selectedItems.includes(id));

    if (allPageSelected) {
      setSelectedItems((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedItems((prev) => [...new Set([...prev, ...pageIds])]);
    }
  }, [paginatedHistory, selectedItems]);

  // Reversal handler
  const handleReverseAirdrop = async () => {
    // Determine which tokens to reverse
    let tokenIdsToReverse = [];

    if (selectedItems.length > 0) {
      const selectedTransfers = history.filter((h) =>
        selectedItems.includes(h.id),
      );
      tokenIdsToReverse = [
        ...new Set(selectedTransfers.map((h) => h.tokenId)),
      ].filter(Boolean);
    } else {
      tokenIdsToReverse = uniqueTokenIds;
    }

    if (tokenIdsToReverse.length === 0) {
      showNotification("No token IDs found to reverse", "error");
      return;
    }

    setReversing(true);
    try {
      const result = await postRequest(
        "/admin/agentic_payment_airdrop/reverse_airdrop",
        { token_ids: tokenIdsToReverse },
      );

      if (result.success) {
        const data = result.data || {};
        showNotification(
          `Reversal complete: ${data.successCount || 0} reversed, ${
            data.failureCount || 0
          } failed`,
          data.failureCount > 0 ? "warning" : "success",
        );
        setSelectedItems([]);
        setShowConfirmModal(false);
        fetchHistory();
      } else {
        const errorMsg = result.error || result.message || "Reversal failed";
        showNotification(
          Array.isArray(errorMsg) ? errorMsg.join(", ") : errorMsg,
          "error",
        );
      }
    } catch (err) {
      showNotification(err?.message || "Reversal failed", "error");
    } finally {
      setReversing(false);
    }
  };

  const openConfirmModal = () => {
    if (filteredHistory.length === 0) {
      showNotification("No airdrops available to reverse", "error");
      return;
    }
    setShowConfirmModal(true);
  };

  // Page selection check
  const pageAllSelected =
    paginatedHistory.length > 0 &&
    paginatedHistory.every((h) => selectedItems.includes(h.id));

  // Warning items for the info box
  const warningItems = [
    "Reversal requires recipients to have sufficient token balance",
    "If tokens have been spent or transferred, the reversal will fail for those recipients",
    "Failed reversals will be reported but won't affect other transfers in the batch",
    "This action cannot be undone once confirmed",
  ];

  // Loading state
  if (loading) {
    return (
      <div className="reverse-airdrop">
        <LoadingSpinner message="Loading reversible airdrops..." />
        <style>{spinnerKeyframes}</style>
      </div>
    );
  }

  return (
    <div className="reverse-airdrop">
      {/* Header */}
      <SectionHeader
        title="Reverse Airdrop"
        subtitle="Select airdrops to reverse. Tokens will be returned from recipients back to the payout account."
        variant="danger"
      >
        <GhostButton onClick={fetchHistory}>Refresh</GhostButton>
      </SectionHeader>

      {/* API Message */}
      {apiMessage && (
        <WarningBox style={{ marginBottom: "20px" }}>{apiMessage}</WarningBox>
      )}

      {/* Stats */}
      <ReverseStats
        history={history}
        selectedItems={selectedItems}
        filteredHistory={filteredHistory}
      />

      {/* Filters */}
      <FilterBar>
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search by recipient or transaction..."
        />
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {selectedItems.length > 0 && (
            <SecondaryButton onClick={() => setSelectedItems([])}>
              Clear Selection
            </SecondaryButton>
          )}
          <DangerButton
            onClick={openConfirmModal}
            disabled={reversing || filteredHistory.length === 0}
          >
            Reverse{" "}
            {selectedItems.length > 0
              ? `${selectedItems.length} Selected`
              : "All"}
          </DangerButton>
        </div>
      </FilterBar>

      {/* Results Info */}
      <ResultsInfo
        totalCount={filteredHistory.length}
        selectedCount={selectedItems.length}
      />

      {/* Content */}
      {filteredHistory.length === 0 ? (
        <EmptyState
          message={
            searchQuery
              ? "No airdrops match your search"
              : "No successful airdrops available to reverse. Execute an airdrop first, then successful transfers will appear here."
          }
        />
      ) : (
        <>
          <ReverseTable
            transfers={paginatedHistory}
            selectedItems={selectedItems}
            onToggleSelect={toggleSelectItem}
            onSelectPage={selectPageItems}
            pageAllSelected={pageAllSelected}
            hederaNetwork={hederaNetwork}
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={filteredHistory.length}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        </>
      )}

      {/* Warning Box */}
      <WarningBox title="Important Information" style={{ marginTop: "20px" }}>
        <WarningList items={warningItems} />
      </WarningBox>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleReverseAirdrop}
        title="Confirm Reversal"
        message={
          <>
            You are about to reverse{" "}
            <strong style={{ color: colors.danger }}>
              {selectedItems.length > 0
                ? `${selectedItems.length} selected`
                : "all"}
            </strong>{" "}
            airdrop{selectedItems.length !== 1 ? "s" : ""}.
          </>
        }
        warning="This will attempt to transfer tokens back from recipients to the payout account. Recipients must have sufficient balance for this to succeed."
        confirmText="Confirm Reversal"
        loading={reversing}
      />

      {/* CSS for spinner animation */}
      <style>{spinnerKeyframes}</style>
    </div>
  );
};

export default ReverseAirdrop;
